import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import { Category, CategoryPayload, CategoriesState } from "types/category";
import { extractUploadedImageUrl } from "store/slices/marketSlice";

type ApiResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

export const fetchCategories = createAsyncThunk<
  Category[],
  void,
  { rejectValue: string }
>("categories/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiResponse<Category[]>>(
      API_ENDPOINTS.categories.list
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to fetch categories."
      );
    }

    return response.data.data || [];
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const uploadCategoryImage = createAsyncThunk<
  string,
  File,
  { rejectValue: string }
>("categories/uploadCategoryImage", async (file, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInstance.post(
      API_ENDPOINTS.upload.image,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const uploadedUrl = extractUploadedImageUrl(response.data);

    if (!uploadedUrl) {
      return rejectWithValue("Image upload failed. Please try again.");
    }

    return uploadedUrl;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const createCategory = createAsyncThunk<
  Category,
  CategoryPayload,
  { rejectValue: string }
>("categories/createCategory", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApiResponse<Category>>(
      API_ENDPOINTS.categories.create,
      payload
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to create category."
      );
    }

    if (!response.data.data) {
      return rejectWithValue("Unable to create category.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const updateCategory = createAsyncThunk<
  Category,
  { id: number; payload: CategoryPayload },
  { rejectValue: string }
>("categories/updateCategory", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ApiResponse<Category>>(
      API_ENDPOINTS.categories.update(id),
      payload
    );

    if (response.data.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to update category."
      );
    }

    if (!response.data.data) {
      return rejectWithValue("Unable to update category.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const deleteCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("categories/deleteCategory", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<ApiResponse<unknown>>(
      API_ENDPOINTS.categories.delete(id)
    );

    if (response.data?.success === false) {
      return rejectWithValue(
        response.data.message || "Unable to delete category."
      );
    }

    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: CategoriesState = {
  categories: [],
  selectedCategory: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  uploadLoading: false,
  error: null,
};

const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearCategoryError: (state) => {
      state.error = null;
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCategories.fulfilled,
        (state, action: PayloadAction<Category[]>) => {
          state.categories = action.payload;
          state.loading = false;
        }
      )
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch categories.";
      })
      .addCase(uploadCategoryImage.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadCategoryImage.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadCategoryImage.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload || "Image upload failed. Please try again.";
      })
      .addCase(createCategory.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
        state.actionLoading = false;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to create category.";
      })
      .addCase(updateCategory.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.categories = state.categories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        state.selectedCategory = action.payload;
        state.actionLoading = false;
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to update category.";
      })
      .addCase(deleteCategory.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(
          (category) => category.id !== action.payload
        );
        if (state.selectedCategory?.id === action.payload) {
          state.selectedCategory = null;
        }
        state.actionLoading = false;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to delete category.";
      });
  },
});

export const { clearCategoryError, clearSelectedCategory } =
  categorySlice.actions;
export default categorySlice.reducer;
